import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import './ConfirmDialog.css';

/**
 * Globální potvrzovací dialog — náhrada `window.confirm`, aby otázky vypadaly
 * jako zbytek systému (a daly se stylovat a rozšiřovat).
 *
 * Použití odkudkoli:
 *   import { confirm } from '../components/ConfirmDialog';
 *   if (!await confirm({ title: 'Smazat kontakt?', message: 'Tahle akce je nevratná.' })) return;
 *
 * Hostitel `<ConfirmDialog />` se vykresluje jednou v App.js, stejně jako
 * ToastNotification. Dokud není připojený (login, testy), spadne to zpátky
 * na nativní `window.confirm`, ať se na odpověď nikdy nečeká zbytečně.
 */

const UDALOST = 'app:confirm';

// Nastaví hostitel při připojení; podle toho se pozná, že se má na co ptát.
let hostPripojen = false;

const VYCHOZI = {
  title: 'Potvrzení',
  message: '',
  confirmText: 'Ano',
  cancelText: 'Ne',
  variant: 'primary',
  icon: 'bi-question-circle',
};

function ConfirmDialog() {
  const [fronta, setFronta] = useState([]);
  const potvrzovaciBtn = useRef(null);
  const aktualni = fronta[0] || null;

  useEffect(() => {
    hostPripojen = true;
    const handler = (e) => setFronta(prev => [...prev, { ...VYCHOZI, ...(e.detail || {}) }]);
    window.addEventListener(UDALOST, handler);
    return () => {
      hostPripojen = false;
      window.removeEventListener(UDALOST, handler);
    };
  }, []);

  // Odpověď pošleme volajícímu a dialog odbavíme; další ve frontě naskočí sám.
  const odpovez = useCallback((odpoved) => {
    setFronta(prev => {
      const [prvni, ...zbytek] = prev;
      if (prvni?.resolve) prvni.resolve(odpoved);
      return zbytek;
    });
  }, []);

  if (!aktualni) return null;

  return (
    <Modal
      show
      onHide={() => odpovez(false)}
      backdrop="static"
      centered
      className="confirm-dialog"
      backdropClassName="confirm-dialog-backdrop"
      onEntered={() => potvrzovaciBtn.current?.focus()}
    >
      <Modal.Header closeButton>
        <Modal.Title className="h6 mb-0">{aktualni.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="d-flex gap-3">
        <i className={`bi ${aktualni.icon} fs-4 text-${aktualni.variant}`}></i>
        <div style={{ whiteSpace: 'pre-line' }}>{aktualni.message}</div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={() => odpovez(false)}>
          {aktualni.cancelText}
        </Button>
        <Button ref={potvrzovaciBtn} variant={aktualni.variant} onClick={() => odpovez(true)}>
          {aktualni.confirmText}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/**
 * Zeptá se uživatele a vrátí Promise<boolean>.
 *
 * @param {{title?: string, message?: string, confirmText?: string,
 *          cancelText?: string, variant?: string, icon?: string}} options
 */
export function confirm(options = {}) {
  if (!hostPripojen) {
    const { title, message } = { ...VYCHOZI, ...options };
    return Promise.resolve(window.confirm([title, message].filter(Boolean).join('\n\n')));
  }

  return new Promise((resolve) => {
    window.dispatchEvent(new CustomEvent(UDALOST, { detail: { ...options, resolve } }));
  });
}

/** Potvrzení nevratné akce — červené tlačítko, výchozí text „Smazat". */
export function confirmDelete(options = {}) {
  return confirm({
    variant: 'danger',
    icon: 'bi-exclamation-triangle',
    confirmText: 'Smazat',
    cancelText: 'Zrušit',
    ...options,
  });
}

export default ConfirmDialog;
